import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { fecha } from "./calendario";

const SHEET = "FE Turnos";
const TABLE = "Checadas";
const EMPLEADOS = ["Carlos Herrera", "Lucía Morales", "Andrés Flores", "Valeria Ruiz"] as const;
const TARIFA = 85; // pesos por hora

/** Minutes → fraction of a day, the way Excel stores times. */
const hm = (h: number, m = 0) => (h * 60 + m) / 1440;

const rnd = seededRandom(5005);
// Empleado, Fecha, Turno, Hora programada, Entrada, Salida, Comida (min)
const ROWS = Array.from({ length: 28 }, (_, i) => {
  const empleado = EMPLEADOS[i % EMPLEADOS.length];
  const nocturno = empleado === "Andrés Flores" || empleado === "Valeria Ruiz" && i % 3 === 0;
  const programada = nocturno ? hm(22) : hm(8);
  // Never exactly at the 10-minute tolerance, where float rounding could flip the answer.
  const retraso = rnd.int(-10, 25);
  const entradaMin = (nocturno ? 22 * 60 : 8 * 60) + (retraso === 10 ? 11 : retraso);
  const duracion = rnd.int(7 * 60 + 30, 11 * 60);
  const salidaMin = (entradaMin + duracion) % 1440;
  return [
    empleado,
    fecha(2026, 3, 2) + Math.floor(i / EMPLEADOS.length),
    nocturno ? "Nocturno" : "Diurno",
    programada,
    entradaMin / 1440,
    salidaMin / 1440,
    rnd.pick([30, 45, 60]),
  ] as [string, number, string, number, number, number, number];
});

type Row = (typeof ROWS)[number];
const brutas = (r: Row) => (((r[5] - r[4]) % 1) + 1) % 1;
const netas = (r: Row) => brutas(r) - r[6] / 1440;
const decimales = (r: Row) => Math.round(netas(r) * 24 * 100) / 100;
const IN = { empleado: "Andrés Flores" };

export const spec: FuncionProjectSpec = {
  projectId: "funciones-turnos",
  sheets: [
    {
      name: SHEET,
      title: "Control de asistencia",
      subtitle: "Checadas de dos semanas — turnos diurnos y nocturnos (algunos cruzan la medianoche)",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Empleado", "Fecha", "Turno", "Hora programada", "Entrada", "Salida", "Comida (min)"],
          rows: ROWS,
          answerColumns: ["Horas brutas", "Horas netas", "Horas decimales", "Puntualidad", "Horas extra", "Pago del día"],
          formats: {
            Fecha: "ddd dd/mm",
            "Hora programada": "hh:mm",
            Entrada: "hh:mm",
            Salida: "hh:mm",
            "Horas brutas": "[h]:mm",
            "Horas netas": "[h]:mm",
            "Horas decimales": "0.00",
            "Horas extra": "0.00",
            "Pago del día": "$#,##0.00",
          },
        },
      ],
      inputs: [
        { address: "P4", label: "Tarifa por hora", value: TARIFA, format: "$#,##0.00" },
        { address: "P5", label: "Empleado", value: IN.empleado },
      ],
      answers: [{ address: "P8", label: "Tarea 7 · Total del empleado", format: "[h]:mm" }],
    },
  ],
  checks: {
    1: { kind: "column", sheet: SHEET, table: TABLE, column: "Horas brutas", fns: ["MOD"], expected: ROWS.map(brutas) },
    2: { kind: "column", sheet: SHEET, table: TABLE, column: "Horas netas", fns: ["MOD"], expected: ROWS.map(netas) },
    3: { kind: "column", sheet: SHEET, table: TABLE, column: "Horas decimales", fns: ["ROUND"], expected: ROWS.map(decimales) },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Puntualidad", fns: ["IF", "TIME"], exact: true,
      expected: ROWS.map((r) => (r[4] > r[3] + hm(0, 10) ? "Retardo" : "A tiempo")),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Horas extra", fns: ["MAX"],
      expected: ROWS.map((r) => Math.max(0, decimales(r) - 8)),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Pago del día", fns: ["MIN", "MAX"],
      expected: ROWS.map((r) => Math.min(8, decimales(r)) * TARIFA + Math.max(0, decimales(r) - 8) * TARIFA * 2),
    },
    7: {
      kind: "cell", sheet: SHEET, address: "P8", fns: ["SUMIFS"],
      expected: ROWS.filter((r) => r[0] === IN.empleado).reduce((s, r) => s + netas(r), 0),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-turnos",
  title: "Horas y turnos: Control de asistencia",
  subtitle: "Restar horas, cruzar la medianoche y pagar horas extra",
  description:
    "Llevas el control de asistencia de cuatro empleados durante dos semanas. Algunos trabajan de noche: entran a las 22:00 y salen a las 6:00 del día siguiente, y ahí una resta normal da un número negativo.\n\nPara Excel una hora es una fracción del día: 12:00 es 0.5. Aprenderás a restar horas que cruzan la medianoche, descontar la comida, convertir a horas decimales para pagar, detectar retardos y separar horas normales de horas extra.",
  topics: [
    "Una hora es una fracción del día",
    "RESIDUO para turnos que cruzan la medianoche",
    "Descontar minutos (÷ 1440)",
    "Horas → decimales (× 24)",
    "NSHORA para comparar contra un horario",
    "Formato [h]:mm para totales de más de 24 horas",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Horas trabajadas",
      description:
        "En la hoja <strong>FE Turnos</strong>, columna <strong>Horas brutas</strong>: tiempo entre <strong>Entrada</strong> y <strong>Salida</strong>. Ojo: en los turnos nocturnos la salida es <strong>menor</strong> que la entrada (sale al día siguiente) y Salida − Entrada da negativo.",
      solution: [
        `Selecciona ${F("H5")}.`,
        `${F("RESIDUO(número,1)")} se queda solo con la parte decimal y convierte un negativo como −0.67 en 0.33, que son las horas reales del turno.`,
        `Escribe ${F("=RESIDUO([@Salida]-[@Entrada],1)")}.`,
        "La columna tiene formato [h]:mm.",
      ],
      expectedResult: "8:15 para una entrada a las 22:00 y salida a las 6:15",
      finalFormula: "=RESIDUO([@Salida]-[@Entrada],1)",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Horas netas",
      description: "En la columna <strong>Horas netas</strong>: las horas brutas <strong>menos</strong> los minutos de <strong>Comida</strong>.",
      solution: [
        `Selecciona ${F("I5")}.`,
        "Los minutos hay que convertirlos a fracción de día: un día tiene 24 × 60 = 1440 minutos.",
        `Escribe ${F("=RESIDUO([@Salida]-[@Entrada],1)-[@[Comida (min)]]/1440")}.`,
      ],
      expectedResult: "Horas brutas − comida",
      finalFormula: "=RESIDUO([@Salida]-[@Entrada],1)-[@[Comida (min)]]/1440",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Horas en decimales",
      description:
        "En la columna <strong>Horas decimales</strong>: convierte las horas netas a número decimal (7:45 → <strong>7.75</strong>), redondeado a <strong>2 decimales</strong>. Así se pueden multiplicar por una tarifa.",
      solution: [
        `Selecciona ${F("J5")}.`,
        "Multiplica por 24: si 1 = un día, 1 × 24 = 24 horas.",
        `Escribe ${F("=REDONDEAR([@[Horas netas]]*24,2)")}.`,
      ],
      expectedResult: "7.75",
      finalFormula: "=REDONDEAR([@[Horas netas]]*24,2)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: ¿Llegó a tiempo?",
      description: conditions("En la columna <strong>Puntualidad</strong>: compara la Entrada con la <strong>Hora programada</strong>, con <strong>10 minutos de tolerancia</strong>:", [
        "Entrada después de la hora programada + 10 minutos → <strong>Retardo</strong>",
        "En otro caso → <strong>A tiempo</strong>",
      ]),
      solution: [
        `Selecciona ${F("K5")}.`,
        `${F("NSHORA(0,10,0)")} son 10 minutos expresados como hora.`,
        `Escribe ${F('=SI([@Entrada]>[@[Hora programada]]+NSHORA(0,10,0),"Retardo","A tiempo")')}.`,
      ],
      expectedResult: "Retardo / A tiempo",
      finalFormula: '=SI([@Entrada]>[@[Hora programada]]+NSHORA(0,10,0),"Retardo","A tiempo")',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Horas extra",
      description: "En la columna <strong>Horas extra</strong>: las horas decimales que pasan de <strong>8</strong>. Si trabajó 8 o menos, <strong>0</strong> (nunca negativo).",
      solution: [
        `Selecciona ${F("L5")}.`,
        "MAX(0, …) evita los negativos sin necesidad de un SI.",
        `Escribe ${F("=MAX(0,[@[Horas decimales]]-8)")}.`,
      ],
      expectedResult: "0 a 3 horas",
      finalFormula: "=MAX(0,[@[Horas decimales]]-8)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Pago del día",
      description: conditions("En la columna <strong>Pago del día</strong>, con la tarifa de <strong>P4</strong>:", [
        "Hasta <strong>8 horas</strong> → tarifa normal",
        "Las horas que pasan de 8 → <strong>tarifa doble</strong>",
      ]),
      solution: [
        `Selecciona ${F("M5")}.`,
        `${F("MIN(8,horas)")} son las horas normales y ${F("MAX(0,horas-8)")} las extra.`,
        `Escribe ${F("=MIN(8,[@[Horas decimales]])*$P$4+MAX(0,[@[Horas decimales]]-8)*$P$4*2")}.`,
      ],
      expectedResult: "Pago normal + extra al doble",
      finalFormula: "=MIN(8,[@[Horas decimales]])*$P$4+MAX(0,[@[Horas decimales]]-8)*$P$4*2",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Total de horas de un empleado",
      description:
        "En <strong>P8</strong>: suma las <strong>Horas netas</strong> del empleado de <strong>P5</strong> en las dos semanas. La celda tiene formato <strong>[h]:mm</strong>: con hh:mm normal, un total de 75 horas se vería como 03:00.",
      solution: [
        `Selecciona ${F("P8")}.`,
        `Escribe ${F("=SUMAR.SI.CONJUNTO(Checadas[Horas netas],Checadas[Empleado],P5)")}.`,
        "Prueba cambiar el formato de P8 a hh:mm y mira cómo \"pierde\" los días completos.",
      ],
      expectedResult: "Más de 50 horas",
      finalFormula: "=SUMAR.SI.CONJUNTO(Checadas[Horas netas],Checadas[Empleado],P5)",
    },
  ],
};
